import React, { useCallback, useState } from 'react';
import { useStore } from '@/lib/store';
import { UserCog, HelpCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from './tooltip';

export const ImpersonationBanner: React.FC = () => {
    const isImpersonating = useStore(state => state.isImpersonating);
    const activeProfileId = useStore(state => state.activeProfileId);
    const profiles = useStore(state => state.profiles);
    const userRole = useStore(state => state.userRole);
    const authUser = useStore(state => state.authUser);
    const stopImpersonation = useStore(state => state.stopImpersonation);
    const [returning, setReturning] = useState(false);

    // Find the active profile to display name
    const activeProfile = profiles.find(p => p.id === activeProfileId);
    const isViewingOwnProfile = Boolean(authUser?.id && activeProfile?.id === authUser.id);

    // Only show if explicitly impersonating OR if a coach is viewing a coached athlete
    const isCoachedProfile = activeProfile?.relation === 'coached';
    const shouldShow = !isViewingOwnProfile && (
        isImpersonating
        || ((userRole === 'coach' || userRole === 'admin' || userRole === 'superadmin') && isCoachedProfile)
    );

    const handleReturn = useCallback(async () => {
        setReturning(true);
        try {
            await stopImpersonation();
        } finally {
            setReturning(false);
        }
    }, [stopImpersonation]);

    if (!shouldShow || !activeProfile) return null;

    return (
        <div className={cn(
            "bg-amber-100 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800",
            "px-4 py-2 flex items-center justify-center text-sm font-medium text-amber-900 dark:text-amber-100",
            "transition-all duration-300 ease-in-out"
        )}>
            <UserCog className="w-4 h-4 mr-2 text-amber-600 dark:text-amber-400" />
            <div className="flex items-center gap-1">
                <span>
                    Logged in as <span className="font-semibold">{authUser?.email || 'Coach'}</span>
                    <span className="mx-2 text-amber-400">|</span>
                    Viewing: <span className="font-bold underline">{activeProfile.displayName}</span>
                </span>

                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button className="ml-1 p-1 hover:bg-amber-200/50 dark:hover:bg-amber-800/50 rounded-full transition-colors">
                                <HelpCircle className="w-3.5 h-3.5 text-amber-500" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="w-80 p-3">
                            <div className="space-y-2">
                                <p className="font-bold border-b pb-1">Debugging Information</p>
                                <div className="grid grid-cols-[80px_1fr] gap-x-2 gap-y-1 text-xs font-mono">
                                    <span className="text-muted-foreground">Auth UID:</span>
                                    <span className="truncate" title={authUser?.id}>{authUser?.id || 'N/A'}</span>

                                    <span className="text-muted-foreground">Profile ID:</span>
                                    <span className="truncate" title={activeProfile.id}>{activeProfile.id}</span>

                                    <span className="text-muted-foreground">Intervals:</span>
                                    <span>{activeProfile.intervalsAthleteId}</span>

                                    <span className="text-muted-foreground">Relation:</span>
                                    <span>{activeProfile.relation}</span>

                                    <span className="text-muted-foreground">Role:</span>
                                    <span>{userRole}</span>
                                </div>
                            </div>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>

                <button
                    onClick={handleReturn}
                    disabled={returning}
                    className={cn(
                        "ml-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold transition-colors",
                        "bg-amber-600 text-white hover:bg-amber-700",
                        "disabled:opacity-50 disabled:cursor-not-allowed"
                    )}
                >
                    {returning ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                        <ArrowLeft className="w-3 h-3" />
                    )}
                    Back to My Profile
                </button>
            </div>
        </div>
    );
};
